import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * Reusable ⓘ help icon for settings the user may not immediately understand.
 * `description` is the one-line "what it controls" summary; `impact` is an
 * optional bullet list ("If disabled: ..."); `saveRequired` and `scope`
 * ("future" | "existing" | "both") render as small trailing notes so every
 * tooltip answers the same four questions consistently instead of each
 * page inventing its own copy/format.
 */
export default function InfoTooltip({ description, impact, saveRequired, scope, defaultValue }) {
  const scopeText = {
    future: 'Affects future events/captures only — existing data is unchanged.',
    existing: 'Also affects already-recorded data.',
    both: 'Affects both future behavior and existing records.',
  }[scope];

  return (
    <Tooltip
      title={
        <Box sx={{ maxWidth: 280 }}>
          <Typography variant="caption" display="block">{description}</Typography>
          {defaultValue && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }}>
              Default: {defaultValue}
            </Typography>
          )}
          {impact && impact.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
              {impact.map((line, i) => (
                <li key={i}><Typography variant="caption">{line}</Typography></li>
              ))}
            </Box>
          )}
          {scopeText && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }}>
              {scopeText}
            </Typography>
          )}
          {saveRequired !== undefined && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }}>
              {saveRequired ? 'Requires clicking Save to take effect.' : 'Takes effect immediately.'}
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="top"
    >
      <InfoOutlinedIcon
        fontSize="inherit"
        sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'middle', color: 'text.secondary', cursor: 'help' }}
        aria-label="More info"
        tabIndex={0}
      />
    </Tooltip>
  );
}
